import { useState, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { zIndex } from 'variables';
import type { AdvisorChatViewModel } from 'autogen/swagger/all';
import ChatButton from './chatButton';
import { dialog, dialogCss } from './chatConstants';
import ChatDialog from './chatDialog';
import { GA4Tracking } from 'modules/tracking/GA4/GA4Tracking';
import { useChatStore } from './ChatStore';

const Container = styled.div`
    position: fixed;
    bottom: 0;
    right: 0;
    width: 100%;
    height: 100%;
    z-index: ${zIndex.dialog};

    ${dialog.breakpoint} {
        ${dialogCss}
    }
`;

const BackDrop = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.75);

    ${dialog.breakpoint} {
        background-color: transparent;
    }
`;

const Chat = (props: { model: AdvisorChatViewModel }): JSX.Element => {
    const {
        model: { openChatOnLoad, sections }
    } = props;
    const [showDialog, setShowDialog] = useState(false);
    const [chatOpen, setChatOpen] = useState(openChatOnLoad);
    const setHeroCtaData = useChatStore((state) => state.setHeroCtaData);

    const toggleChat = useCallback(() => {
        if (chatOpen) {
            if (window.kindlyChat) {
                window.kindlyChat.closeChat();
            }
            setChatOpen(false);
            setShowDialog(false);

            GA4Tracking({
                eventName: 'minimize_chat',
                context: 'chat',
                category: 'advisory',
                eventParams: [{ param_name: 'chat_step', param_value: 'step_1' }]
            });
        } else {
            setChatOpen(true);
            setShowDialog(true);

            GA4Tracking({
                eventName: 'open_chat',
                context: 'chat',
                category: 'advisory',
                eventParams: [{ param_name: 'chat_step', param_value: 'step_1' }]
            });
        }
    }, [chatOpen]);

    const minimizeDialog = useCallback(() => {
        setShowDialog(false);
    }, [showDialog]);

    const minimizeChat = useCallback(() => {
        setChatOpen(false);
        setShowDialog(false);

        GA4Tracking({
            eventName: 'minimize_chat',
            context: 'chat',
            category: 'advisory',
            eventParams: [{ param_name: 'chat_step', param_value: 'step_1' }]
        });
    }, []);

    useEffect(() => {
        setHeroCtaData(props.model);
    }, []);

    // Used to open chat from elements outside the chat (add a "open-chat" class to the element you wish to use to open the chat)
    useEffect(() => {
        function jsHookClick(event) {
            const target = event.target as HTMLElement;
            const jsHookOpenChat = target.closest('.open-chat');

            if (jsHookOpenChat) {
                setChatOpen(true);
                setShowDialog(true);
            }
        }

        document.addEventListener('click', jsHookClick);

        return (): void => {
            document.removeEventListener('click', jsHookClick);
        };
    }, []);

    return (
        <>
            {showDialog && (
                <Container>
                    <BackDrop />
                    <ChatDialog
                        showSelect={showDialog}
                        minimizeDialog={minimizeDialog}
                        minimizeChat={minimizeChat}
                        sections={sections}
                        setChatOpen={setChatOpen}
                        setShowDialog={setShowDialog}
                    />
                </Container>
            )}

            <ChatButton toggleChat={toggleChat} chatOpen={chatOpen} />
        </>
    );
};

export default Chat;
